import React from 'react';
import styled, { keyframes } from 'styled-components';
import { TokenSet } from '../../styles/tokens';
import { Button } from './Button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from './Toast';

const PIX_KEY = '00020126400014br.gov.bcb.pix0118fahsoder@gmail.com5204000053039865802BR5913FABIANO SODER6009Sao Paulo62290525REC69FAAB538396E9034097936304250B';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 18px;
  padding: 32px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const Title = styled.h2<{ color: string }>`
  font-size: 22px;
  font-weight: 800;
  color: ${p => p.color};
  text-align: center;
  margin-bottom: 6px;
`;

const Subtitle = styled.p<{ color: string }>`
  font-size: 14px;
  color: ${p => p.color};
  text-align: center;
  margin-bottom: 22px;
`;

const QrFrame = styled.div<{ tokens: TokenSet }>`
  background: #ffffff;
  border: 1px solid ${p => p.tokens.border};
  border-radius: 14px;
  padding: 14px;
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
`;

const QrImg = styled.img`
  width: 220px;
  height: 220px;
  display: block;
  object-fit: contain;
`;

const KeyLabel = styled.div<{ color: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.color};
  margin-bottom: 6px;
  text-align: center;
`;

const KeyBox = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgElevated};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 10px;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 11px;
  color: ${p => p.tokens.textMuted};
  word-break: break-all;
  line-height: 1.4;
  max-height: 80px;
  overflow-y: auto;
  margin-bottom: 14px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

interface Props {
  tokens: TokenSet;
  onClose: () => void;
}

export const DonateModal: React.FC<Props> = ({ tokens, onClose }) => {
  const { t } = useLanguage();
  const { showToast } = useToast();

  const handleCopyKey = () => {
    navigator.clipboard.writeText(PIX_KEY);
    showToast(t.donateKeyCopied, 'success');
  };

  return (
    <Backdrop onClick={onClose}>
      <Modal tokens={tokens} onClick={e => e.stopPropagation()}>
        <Title color={tokens.text}>{t.donateModalTitle}</Title>
        <Subtitle color={tokens.textMuted}>{t.donateModalSubtitle}</Subtitle>

        <QrFrame tokens={tokens}>
          <QrImg src="/pix-qr.png" alt="PIX QR Code" />
        </QrFrame>

        <KeyLabel color={tokens.textMuted}>{t.donateKeyLabel}</KeyLabel>
        <KeyBox tokens={tokens}>{PIX_KEY}</KeyBox>

        <Actions>
          <Button tokens={tokens} variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            {t.donateClose}
          </Button>
          <Button tokens={tokens} onClick={handleCopyKey} style={{ flex: 2 }}>
            {t.donateCopyKey}
          </Button>
        </Actions>
      </Modal>
    </Backdrop>
  );
};
